import { ClaimStage } from './billing';

export interface RPMNote {
  id: number;
  status: string;
  originalContent: { header: unknown; sections: unknown; footer: unknown };
  currentContent: { header: unknown; sections: unknown; footer: unknown };
  currentEditableContent?: { header: string; body: string; footer: string };
  hasBeenModified: boolean;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  lastModifiedByUserUuid?: string;
  lastModifiedByUserFullName?: string;
  signedAt?: string;
  signedBy?: string;
  pdfUrl?: string;
  metadata?: unknown;
  claimId: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface RPMNoteDevice {
  name: string;
  type: string;
}

export interface RPMNoteCPTCode {
  code: string;
  description?: string;
}

export interface RPMNoteInteraction {
  date: string;
  type: string;
  description: string;
}

export interface RPMNoteMedication {
  name: string;
  dispense: number;
  sig: string;
  isPRN?: boolean;
  indication?: string | null;
}

export interface RPMNoteAllergy {
  allergen: string;
  reaction?: string;
}

export interface RPMNoteDiagnosis {
  code: string;
  description: string;
  type: string;
}

export interface RPMNotePlanItem {
  type: string;
  description: string;
  cptCode?: string;
  modifiers?: string;
  quantity?: number;
}

export interface RPMNoteSubSection {
  key: string;
  content: string | RPMNoteSectionContent;
  isSubSection?: boolean;
  isTable?: boolean;
}

export type RPMNoteSectionContent =
  | string
  | RPMNoteDevice[]
  | RPMNoteCPTCode[]
  | RPMNoteInteraction[]
  | RPMNoteMedication[]
  | RPMNoteAllergy[]
  | RPMNoteDiagnosis[]
  | RPMNotePlanItem[]
  | RPMNoteSubSection[];

export interface RPMNoteSection {
  order: number;
  title: string;
  content?: RPMNoteSectionContent;
  isSingleLine?: boolean;
  isSubSection?: boolean;
  isTable?: boolean;
  key: string;
}

export interface RPMNoteFooter {
  centerText: string;
  poweredBy: string;
  datetime: string;
  providerFullName: string;
}

export interface RPMNoteHeader {
  patient: string;
  dateOfBirth: string;
  sex: string;
  chartNumber: string;
  provider: string;
  visitDate: string;
}

export interface RPMNoteContent {
  header: RPMNoteHeader;
  sections: RPMNoteSection[];
  footer: RPMNoteFooter;
}

export interface RPMNoteContentAsHtml {
  header: string;
  body: string;
  footer: string;
}

export enum RPMNoteSectionKey {
  CHIEF_COMPLAINT = 'chiefComplaint',
  REMOTE_PATIENT_MONITORING = 'remotePatientMonitoring',
  PHYSICIAN_ORDER_DEVICE_INFO = 'physicianOrderAndDeviceInformation',
  DAILY_RECORDINGS_ALERTS = 'dailyRecordingsAndAlerts',
  REMOTE_MONITORING_INTERACTION = 'remotePhysiologicalMonitoringAndInteraction',
  SUMMARY_MONITORING_MANAGEMENT = 'summaryOfMonitoringAndManagement',
  INTERACTION_LOG = 'interactionLog',
  MEDICATIONS_ALLERGIES = 'medicationsAndAllergies',
  ASSESSMENT = 'assessment',
  PLAN = 'plan',
}

export enum RPMNoteSubSectionKey {
  FDA_DEVICES = 'fdaDevices',
  CPT_CODES = 'cptCodes',
  PHYSICIAN_ORDER = 'physicianOrder',
  DEVICE_USAGE = 'deviceUsage',
  MONITORING_PERIOD = 'monitoringPeriod',
  DATA_TRANSMISSION = 'dataTransmission',
  INTERACTION_DETAILS = 'interactionDetails',
  PATIENT_MANAGEMENT = 'patientManagement',
  PATIENT_CONSENT = 'patientConsent',
  MONITORING_DAYS = 'monitoringDays',
  PHYSIOLOGICAL_DATA = 'physiologicalData',
  FEEDBACK = 'feedback',
  CURRENT_MEDICATIONS = 'currentMedications',
  ALLERGIES = 'allergies',
}

export interface BillingPatientsResponse {
  data: BillingMember[];
  pagination: BillingPagination;
}

export interface BillingMember {
  billingCycleRange: BillingCycleRange;
  clinicId: number;
  clinicName: string;
  coverageType: string;
  cptCodesAssociated: string[];
  createdAt: Date;
  firstName: string;
  hintId: string;
  icd10Codes: string[];
  id: number;
  initialMdVisitDate: null;
  initialMedicalAppointmentDate: string;
  insuranceCompany: string;
  insuranceCompanyId: string;
  insuranceCompanyPayerId: string;
  lastDateBillingCycle: null;
  lastExportDate: null;
  lastName: string;
  lastSyncAt: Date;
  memberId: number;
  membershipComputeStatus: string;
  membershipOriginalStatus: string;
  membershipStartDate: string;
  membershipId: string;
  membershipType: string;
  memberUuid: string;
  profileStatus: string;
  profileStatusId: number;
  rpmExcluded: boolean;
  rpmExclusionCriteria: string[];
  rpmExportStatus: string;
  rpmWarnings: string[];
  updatedAt: Date;
  uuid: string;
  clinicPayerConfig?: BillingClinicPayerConfig | null;
}

export interface BillingClinicPayerConfig {
  payerId: string;
  clinicId: number;
  rpmEnabled: boolean;
  cptCodesConfig: { [key: string]: CptCodesConfig };
}

export interface CptCodesConfig {
  enabled: boolean;
  max_claims_per_cycle: number;
  pos: number;
}

export interface BillingCycleRange {
  from: string;
  to: string;
}

export interface BillingPagination {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

export interface BillingCptCodes {
  id: number;
  claimId: number;
  cptCode: string;
  units: number;
}

export interface BillingRPMNote {
  id: number;
  pdfUrl: string;
}

export interface BillingClaimPreview {
  id: number;
  dateOfService: string;
  startDate: string;
  status: string;
  cptCodes: BillingCptCodes[];
  errors: string[];
  warnings: string[];
  memberName: string;
  clinicName: string;
  billingMember: BillingMember;
  placeOfService: string;
  noteId: string;
}

export interface BillingClaim {
  id: number;
  dateOfService: string;
  startDate: string;
  status: string;
  cptCodes: BillingCptCodes[];
  warnings: string[];
  errors: string[];
  placeOfService: string;
  invoiceAmount: string;
  billingMember: BillingMember;
  invoiceId: string;
  rpmNotes: BillingRPMNote;
  processId: number;
  invoiceCreationDate?: string;
}

export interface BillingClaimsPreviewResponse {
  data: BillingClaimPreview[];
  pagination: BillingPagination;
}

export interface BillingClaimsExportedResponse {
  data: BillingClaim[];
  pagination: BillingPagination;
}

export interface BillingNoteResponse {
  success: boolean;
  id: number;
  note: RPMNoteContent;
  html: RPMNoteContentAsHtml;
}

export interface ClaimLog {
  id: number;
  status: string;
  stage: string;
  date: string;
  description: string;
}

export interface BillingClaimLogResponse {
  data: ClaimLog[];
  pagination: BillingPagination;
}

export interface BillingProcess {
  id: number;
  status: ClaimStage;
  startedByUserUuid: string;
  startedByUserFullName: string;
  lastModifiedAt: string;
  lastModifiedByUserUuid: string;
  lastModifiedByUserFullName: string;
  creationParameters: { filters: { clinicIds: number[] } };
  createdAt: string;
  updatedAt: string;
}

// Enhanced process interface with async processing support
export interface EnhancedBillingProcess extends BillingProcess {
  isActive: boolean;
  progressMessage?: string;
  duration?: number;
  errorMessage?: string;
}

// API request configuration for async processing
export interface ProcessExecutionConfig {
  runInBackground: boolean;
}

// Base API request interface with user info
export interface BaseApiRequest {
  user: BillingUser;
  config?: ProcessExecutionConfig;
}

// Background process response from API
export interface BackgroundProcessResponse {
  mode: 'background';
  process: BillingProcess;
}

// Synchronous process response from API  
export interface SyncProcessResponse {
  created?: number;
  failed?: number;
  exported?: number;
  processId: number;
}

// Union type for API responses
export type ProcessApiResponse = BackgroundProcessResponse | SyncProcessResponse;

export interface BillingUser {
  uuid: string;
  fullName: string;
}

export enum DownloadStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface NoteExport {
  id: number;
  status: DownloadStatus;
  format: string;
  fileUrl: string | null;
  error: string | null;
  processedNotes: number;
  totalNotes: number;
  createdByUserFullName: string;
  createdByUserUuid: string;
  createdAt: string;
  updatedAt: string;
  claims: NoteExportClaim[];
}

export interface NoteExportClaim {
  noteExportId: number;
  claimId: number;
}

export interface NoteExportsResponse {
  data: NoteExport[];
  pagination: BillingPagination;
}

export interface GetNoteExportsParams {
  status?: DownloadStatus;
  clinicIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface CptCodeConfig {
  code?: string;
  enabled: boolean;
  max_claims_per_cycle: number;
  pos: number;
}

export interface ClinicConfig {
  id?: number; // Configuration ID
  clinicId: number;
  payerId: string;
  placeOfService: string;
  rpmEnabled: boolean;
  cptCodesConfig: { [key: string]: CptCodeConfig };
}

export interface ClinicConfigUpdate {
  placeOfService?: string;
  rpmEnabled?: boolean;
  cptCodesConfig?: { [key: string]: CptCodeConfig };
  updatedByUuid?: string;
}

export interface ClinicConfigExtended extends ClinicConfig {
  createdAt: string;
  createdByName: string;
  createdByUuid: string | null;
  updatedAt: string;
  updatedBy: string | null;
  updatedByUuid: string | null;
}
export interface ClinicsConfigurationResponse {
  data: ClinicConfigExtended[];
  pagination: BillingPagination;
}
export type CsvRawContent = {
  headers: string[];
  rows: string[][];
};

// Unified Claim interface for GET /claims endpoint (NEW)
export interface UnifiedClaim {
  id: number;
  dateOfService: string;
  startDate: string;
  status: string;
  errors: string[];
  warnings: string[];
  memberUuid: string;
  invoiceId: string | null;
  invoiceAmount: string | null;
  placeOfService: string;
  totalMonitoringTime: string;
  processId: number;
  hasCoreTaskPaTime: boolean;
  hasCoreTaskSchedulingTime: boolean;
  coreTaskPaMinutes: number;
  coreTaskSchedulingMinutes: number;
  createdAt: string;
  updatedAt: string;
  billingMember: BillingMember;
  cptCodes: BillingCptCodes[];
  rpmNotes: RPMNote;
  process: BillingProcess;
}

// Response interface for GET /claims endpoint (NEW)
export interface GetAllClaimsResponse {
  data: UnifiedClaim[];
  pagination: BillingPagination;
}
